// Module: VideoUploadBrick
// Role: Self-contained video upload brick for flow engine.
//       Presign -> XHR upload (progress, abort) -> submit artefact_path.
//       FTR-1315: st.10.1 distribution consent gate before upload.
//       200MB client guard, no preview, explicit Content-Type.
// Uses: hooks/useFileUpload, api/flow-client, shared/limits, atoms/ConsentRow
// Used by: brick-registry.ts (kind='video')
//
// Pattern: UgcPhotoGridScreen upload loop (presign -> XHR -> submit).
// ADR-023: no window.Telegram access, no platform string literals.

import { useContext, useRef, useState, useCallback } from 'react';
import { PlatformContext } from '../../../providers/PlatformProvider';
import { useFileUpload } from '../../../hooks/useFileUpload';
import {
  useArtefactPresign,
  useSubmitCheckpoint,
  useRecordConsents,
} from '../../../api/flow-client';
import { MAX_VIDEO_SIZE_BYTES, ACCEPTED_VIDEO_TYPES } from '../../../shared/limits';
import { Frame } from '../atoms/Frame';
import { MiniHeader } from '../atoms/MiniHeader';
import { InstructionCard } from '../atoms/InstructionCard';
import { ConsentRow } from '../atoms/ConsentRow';
import { WizardCtaBar } from '../../../components/atoms/WizardCtaBar';
import { HintBar } from '../atoms/HintBar';
import { StepStrip } from '../StepStrip';
import { Ic } from '../../../components/atoms/Ic';
import { P, S, T } from '../../../styles/tokens';
import { createUploadFunnel, uploadTimeoutFor } from '../../../lib/uploadTelemetry';
import { UploadTimeoutError } from '../../pantene/_uploadXHR';
import { flowStepLabel } from '../_labels';
import type {
  Checkpoint,
  CheckpointAttempt,
  SlotFlowRun,
  RequiredConsents,
} from '../../../api/flow-types';

const SUCCESS_SOFT_BG = '#EFF7E9';

export interface VideoUploadBrickProps {
  checkpoint: Checkpoint;
  attempt?: CheckpointAttempt;
  flowRun: SlotFlowRun;
  checkpoints: Checkpoint[];
  attempts: CheckpointAttempt[];
  onCta: () => void;
}

/** Extract on_publish scopes + version from checkpoint metadata (data-driven). */
function getPublishConsents(
  checkpoint: Checkpoint,
): { scopes: string[]; version: string } | null {
  const meta = checkpoint.metadata as
    | { required_consents?: RequiredConsents }
    | undefined;
  const rc = meta?.required_consents;
  if (!rc?.on_publish || rc.on_publish.length === 0) return null;
  return { scopes: rc.on_publish, version: rc.version ?? '' };
}

export function VideoUploadBrick({
  checkpoint,
  flowRun,
  checkpoints,
}: VideoUploadBrickProps) {
  const platform = useContext(PlatformContext);
  const canUpload = platform?.capabilities.fileUpload ?? false;

  const [hintMessage, setHintMessage] = useState<string | null>(null);
  const [isDone, setIsDone] = useState(false);
  const [isBusy, setIsBusy] = useState(false);

  // FTR-1315: distribution consent state (st.10.1)
  const publishConsents = getPublishConsents(checkpoint);
  const needsConsent = publishConsents !== null;
  const [consentGiven, setConsentGiven] = useState(false);
  const [consentRecording, setConsentRecording] = useState(false);
  const recordConsents = useRecordConsents();

  const upload = useFileUpload();
  const presign = useArtefactPresign(flowRun.slot_id);
  const submitCheckpoint = useSubmitCheckpoint(flowRun.slot_id, checkpoint.id);

  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const parentCount = checkpoints.filter(
    (c) => c.parent_checkpoint_id === null,
  ).length;

  // FTR-1315: toggle consent and record to backend
  const handleConsentToggle = useCallback(async () => {
    if (consentGiven || !publishConsents) {
      setConsentGiven((v) => !v);
      return;
    }
    setConsentRecording(true);
    try {
      await recordConsents.mutateAsync({
        scopes: publishConsents.scopes,
        version: publishConsents.version,
      });
      setConsentGiven(true);
    } catch {
      setHintMessage('Не удалось записать согласие. Попробуйте ещё раз.');
    } finally {
      setConsentRecording(false);
    }
  }, [consentGiven, publishConsents, recordConsents]);

  const uploadAllowed = !needsConsent || consentGiven;

  const handlePickFile = useCallback(() => {
    if (!canUpload) {
      setHintMessage('Загрузка файлов недоступна на этой платформе.');
      return;
    }
    if (isDone || isBusy || !uploadAllowed) return;
    fileInputRef.current?.click();
  }, [canUpload, isDone, isBusy, uploadAllowed]);

  const handleFileChange = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (fileInputRef.current) fileInputRef.current.value = '';

      // TECH-1356: video upload telemetry funnel
      const funnel = createUploadFunnel(flowRun.slot_id, checkpoint.slug, 'video');
      if (!file) {
        funnel.stage('picker_null');
        funnel.flush('abandoned');
        return;
      }

      // Type guard
      if (
        !ACCEPTED_VIDEO_TYPES.includes(
          file.type as (typeof ACCEPTED_VIDEO_TYPES)[number],
        )
      ) {
        setHintMessage('Неподдерживаемый формат. Выберите MP4 или MOV.');
        funnel.stage('invalid_type', { mime: file.type });
        funnel.flush('failed');
        return;
      }

      // 200 MB size guard (EC-5)
      if (file.size > MAX_VIDEO_SIZE_BYTES) {
        setHintMessage('Файл слишком большой. Максимум 200 МБ.');
        funnel.stage('oversize', { size: file.size });
        funnel.flush('failed');
        return;
      }

      setHintMessage(null);
      setIsBusy(true);

      funnel.stage('file_picked', { mime: file.type, size: file.size });

      try {
        // Presign URL
        funnel.stage('presign_start');
        const presignResult = await presign.mutateAsync({
          checkpoint_slug: checkpoint.slug,
          artefact_kind: 'video',
          content_md5: '',
          file_size_bytes: file.size,
        });
        funnel.stage('presign_done');

        // XHR upload with progress + adaptive timeout
        funnel.stage('upload_start');
        await upload.start({
          url: presignResult.upload_url,
          file,
          timeoutMs: uploadTimeoutFor(file.size),
        });
        funnel.stage('upload_done');

        // Submit checkpoint with real artefact_path (EC-2: non-empty)
        funnel.stage('submit_start');
        await submitCheckpoint.mutateAsync({
          artefact_path: presignResult.artefact_path,
        });
        funnel.stage('submit_done');

        funnel.flush('success');
        setIsDone(true);
      } catch (err: unknown) {
        const isTimeout =
          err instanceof UploadTimeoutError ||
          (err instanceof DOMException && err.name === 'AbortError');
        if (isTimeout) {
          funnel.stage('upload_timeout');
          funnel.flush('timeout');
        } else {
          funnel.stage('upload_error');
          funnel.flush('failed');
        }
        const msg = err instanceof Error ? err.message : 'Неизвестная ошибка';
        setHintMessage(`Ошибка загрузки: ${msg}`);
      } finally {
        setIsBusy(false);
      }
    },
    [checkpoint.slug, flowRun.slot_id, presign, upload, submitCheckpoint],
  );

  return (
    <Frame>
      <MiniHeader
        title={checkpoint.display_name ?? flowStepLabel(checkpoint.kind)}
      />
      <div className="px-4 pt-3 pb-2">
        <StepStrip
          current={checkpoint.position + 1}
          total={parentCount}
          showCaption
        />
      </div>

      <div className="flex-1 overflow-y-auto px-4 pb-4 flex flex-col gap-4">
        <div style={{ paddingTop: 4 }}>
          <div style={{ font: T.title3, color: S.label, lineHeight: 1.2 }}>
            Загрузи видео-отзыв
          </div>
          <div
            style={{
              font: T.footnote,
              color: S.secondaryLabel,
              marginTop: 4,
            }}
          >
            {checkpoint.instructions_text ?? 'MP4 или MOV, до 200 МБ'}
          </div>
        </div>

        {/* FTR-1315: st.10.1 distribution consent (before upload zone) */}
        {needsConsent && (
          <div data-testid="distribution-consent">
            <ConsentRow
              checked={consentGiven}
              onToggle={handleConsentToggle}
              text="Даю согласие на распространение видео на площадках маркетплейсов (WB, Ozon) в рамках задания."
              subtext="Видео будет опубликовано на указанных площадках. Срок — до 90 дней."
              footnote={
                consentRecording
                  ? 'Записываем...'
                  : '* Согласие на распространение (ст.10.1 152-ФЗ)'
              }
            />
          </div>
        )}

        <InstructionCard p={12}>
          {/* Upload zone */}
          <div
            style={{
              borderRadius: 16,
              border: `2px dashed ${isDone ? P.success : P.terracotta}`,
              background: isDone ? SUCCESS_SOFT_BG : P.terraSoft,
              padding: '28px 16px',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 10,
              cursor:
                canUpload && !isDone && !isBusy && uploadAllowed
                  ? 'pointer'
                  : 'default',
              opacity: uploadAllowed ? 1 : 0.5,
            }}
            onClick={
              canUpload && !isDone && !isBusy && uploadAllowed
                ? handlePickFile
                : undefined
            }
            role={
              canUpload && !isDone && !isBusy && uploadAllowed
                ? 'button'
                : undefined
            }
            aria-label={isDone ? 'Видео загружено' : 'Загрузить видео'}
            data-testid="video-upload-zone"
          >
            {isBusy ? (
              <>
                <div
                  style={{
                    width: 26,
                    height: 26,
                    borderRadius: '50%',
                    border: `3px solid ${P.terraSoft}`,
                    borderTopColor: P.terracotta,
                    animation: 'spin 0.8s linear infinite',
                  }}
                />
                <div
                  style={{
                    font: T.subhead,
                    fontWeight: 600,
                    color: S.label,
                  }}
                >
                  {submitCheckpoint.isPending ? 'Проверяем…' : 'Загружаем…'}
                </div>
              </>
            ) : (
              <>
                <Ic
                  name={isDone ? 'check' : 'camera'}
                  size={28}
                  color={isDone ? P.success : P.terracotta}
                />
                <div
                  style={{
                    font: T.subhead,
                    fontWeight: 600,
                    color: isDone ? P.success : S.label,
                  }}
                >
                  {isDone ? 'Видео загружено' : 'Нажми, чтобы выбрать видео'}
                </div>
              </>
            )}
          </div>

          {/* Progress bar */}
          {upload.status === 'uploading' && upload.progress !== null && (
            <div style={{ marginTop: 10 }}>
              <div
                style={{
                  height: 4,
                  borderRadius: 2,
                  background: P.line,
                  overflow: 'hidden',
                }}
              >
                <div
                  style={{
                    height: '100%',
                    width: `${Math.round((upload.progress ?? 0) * 100)}%`,
                    background: P.terracotta,
                    transition: 'width 0.2s linear',
                  }}
                />
              </div>
              <div
                style={{
                  font: T.caption,
                  color: S.secondaryLabel,
                  marginTop: 4,
                  textAlign: 'center',
                }}
              >
                {Math.round((upload.progress ?? 0) * 100)}%
              </div>
            </div>
          )}
        </InstructionCard>

        {!canUpload && (
          <HintBar>Загрузка файлов недоступна на этой платформе.</HintBar>
        )}

        {hintMessage && canUpload && <HintBar>{hintMessage}</HintBar>}

        {isDone && (
          <div
            style={{
              background: SUCCESS_SOFT_BG,
              borderRadius: 12,
              padding: '10px 12px',
              border: '1px solid rgba(45,138,78,0.25)',
              font: T.footnote,
              color: S.label,
              lineHeight: 1.45,
            }}
          >
            Видео отправлено на проверку.
          </div>
        )}
      </div>

      {/* Hidden file input */}
      {canUpload && !isDone && (
        <input
          ref={fileInputRef}
          type="file"
          accept="video/mp4,video/quicktime"
          style={{ display: 'none' }}
          onChange={handleFileChange}
          data-testid="video-input"
          aria-hidden="true"
        />
      )}

      <WizardCtaBar
        label={isDone ? 'Готово' : 'Загрузить видео'}
        onClick={handlePickFile}
        disabled={isDone || isBusy || !canUpload || !uploadAllowed}
      />
    </Frame>
  );
}
